import { FC, useState, useEffect } from 'react'
import { jingqueAdminApi, SystemConfig, ConfigHistory } from '@/api/jingqueAdminApi'

const GROUP_LABELS: Record<string, string> = {
  jingque: '系统',
  'jingque matching': '匹配',
  'jingque questionnaire': '问卷',
  'jingque activity': '活动',
}

export const SystemConfigsPage: FC = () => {
  const [configs, setConfigs] = useState<SystemConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<SystemConfig | null>(null)
  const [showHistory, setShowHistory] = useState<{ key: string; history: ConfigHistory[] } | null>(null)
  const [saving, setSaving] = useState(false)

  const loadConfigs = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await jingqueAdminApi.configs.list()
      setConfigs(res.configs || [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadConfigs() }, [])

  const grouped = () => {
    const map = new Map<string, SystemConfig[]>()
    configs.forEach(c => {
      const prefix = c.key.split('.')[0] || 'other'
      const group = GROUP_LABELS[prefix] || prefix
      if (!map.has(group)) map.set(group, [])
      map.get(group)!.push(c)
    })
    return map
  }

  const toggleGroup = (g: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      next.has(g) ? next.delete(g) : next.add(g)
      return next
    })
  }

  const handleCreate = async (data: { key: string; value: string; valueType: string; description: string }) => {
    setSaving(true)
    try {
      await jingqueAdminApi.configs.create(data)
      setShowCreate(false)
      loadConfigs()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (key: string, data: { value: string; description: string; changeReason: string }) => {
    setSaving(true)
    try {
      await jingqueAdminApi.configs.update(key, data)
      setEditing(null)
      loadConfigs()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (key: string) => {
    if (!confirm(`确认删除配置项 ${key}？`)) return
    try {
      await jingqueAdminApi.configs.delete(key)
      loadConfigs()
    } catch (e: any) {
      alert(e.message)
    }
  }

  const loadHistory = async (key: string) => {
    try {
      const res = await jingqueAdminApi.configs.getHistory(key)
      setShowHistory({ key, history: res.history || [] })
    } catch (e: any) {
      alert(e.message)
    }
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-800">系统配置</h1>
        <div className="flex gap-2">
          <button onClick={loadConfigs} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">刷新</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg">新建配置</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        </div>
      ) : (
        <div className="space-y-4">
          {[...grouped().entries()].map(([group, items]) => (
            <div key={group} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleGroup(group)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
              >
                <span className="font-medium text-gray-700">{group}</span>
                <span className="text-gray-400">{collapsed.has(group) ? '▶' : '▼'}</span>
              </button>
              {!collapsed.has(group) && (
                <table className="w-full text-sm">
                  <thead className="bg-white border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Key</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Value</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">类型</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">描述</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500 w-44">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(c => (
                      <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono text-xs text-gray-800">{c.key}</td>
                        <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{c.value}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{c.valueType}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{c.description || '-'}</td>
                        <td className="px-4 py-2">
                          <button onClick={() => setEditing(c)} className="text-primary hover:underline text-xs mr-3">编辑</button>
                          <button onClick={() => loadHistory(c.key)} className="text-gray-500 hover:underline text-xs mr-3">历史</button>
                          <button onClick={() => handleDelete(c.key)} className="text-red-500 hover:underline text-xs">删除</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          {configs.length === 0 && (
            <div className="text-center py-12 text-gray-400">暂无配置</div>
          )}
        </div>
      )}

      {showCreate && (
        <ConfigModal saving={saving} onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}

      {editing && (
        <ConfigModal config={editing} saving={saving} onSave={(data) => handleUpdate(editing.key, data)} onClose={() => setEditing(null)} />
      )}

      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-medium">变更历史: {showHistory.key}</h2>
              <button onClick={() => setShowHistory(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-4">
              {showHistory.history.length === 0 ? (
                <div className="text-center py-8 text-gray-400">暂无历史记录</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="pb-2">旧值</th>
                      <th className="pb-2">新值</th>
                      <th className="pb-2">操作人</th>
                      <th className="pb-2">原因</th>
                      <th className="pb-2">时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showHistory.history.map(h => (
                      <tr key={h.id} className="border-b border-gray-100">
                        <td className="py-2 font-mono text-xs text-red-600 max-w-xs truncate">{h.oldValue}</td>
                        <td className="py-2 font-mono text-xs text-green-600 max-w-xs truncate">{h.newValue}</td>
                        <td className="py-2 text-gray-600 text-xs">{h.changedBy}</td>
                        <td className="py-2 text-gray-500 text-xs">{h.changeReason || '-'}</td>
                        <td className="py-2 text-gray-400 text-xs">{formatDate(h.changedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const ConfigModal: FC<{
  config?: SystemConfig
  saving: boolean
  onSave: (data: any) => void
  onClose: () => void
}> = ({ config, saving, onSave, onClose }) => {
  const [form, setForm] = useState({
    key: config?.key || '',
    value: config?.value || '',
    valueType: config?.valueType || 'string',
    description: config?.description || '',
    changeReason: '',
  })

  const handleSubmit = () => {
    if (config) {
      onSave({ value: form.value, description: form.description, changeReason: form.changeReason })
    } else {
      onSave({ key: form.key, value: form.value, valueType: form.valueType, description: form.description })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-medium">{config ? '编辑配置' : '新建配置'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Key {config ? '(不可修改)' : ''}</label>
            <input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} disabled={!!config}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono disabled:bg-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
            <textarea value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" rows={4} />
          </div>
          {!config && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">类型</label>
              <input value={form.valueType} onChange={e => setForm(f => ({ ...f, valueType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="string" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
          </div>
          {config && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">变更原因</label>
              <input value={form.changeReason} onChange={e => setForm(f => ({ ...f, changeReason: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="可选" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">取消</button>
          <button onClick={handleSubmit} disabled={saving || !form.key || !form.value} className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50">
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(s: string) {
  if (!s) return '-'
  return new Date(s).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })
}
